import { useMemo } from 'react';
import type { ActiveEvent } from '@/types/dynamicEvents';
import type { GameDate } from '@/types/events';
import { GAME_CONSTANTS } from '@/config/gameConstants';

/** A single news story formatted for display. Reusable for future smartphone news app. */
export interface NewsStory {
  id: string;
  headline: string;
  body: string;
  category: string;
  severity: string;
  gameDate: GameDate;
}

function compareDateDesc(a: GameDate, b: GameDate): number {
  if (a.year !== b.year) return b.year - a.year;
  if (a.month !== b.month) return b.month - a.month;
  return b.day - a.day;
}

/**
 * Selects and formats the most recent events for newspaper display.
 * Accepts eventHistory as a parameter so the parent controls where the data comes from.
 */
export function useNewspaperEvents(eventHistory: ActiveEvent[]) {
  return useMemo(() => {
    const sorted = [...(eventHistory ?? [])].sort((a, b) => {
      // Sort by gameDate descending, then triggeredAt descending
      const dateCmp = compareDateDesc(a.gameDate, b.gameDate);
      if (dateCmp !== 0) return dateCmp;
      return new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime();
    });

    const top = sorted.slice(0, GAME_CONSTANTS.newspaper.maxEvents);

    const stories: NewsStory[] = top.map((ae) => ({
      id: ae.event.id,
      headline: ae.event.newsHeadline || ae.event.title,
      body: ae.event.description,
      category: ae.event.category,
      severity: ae.event.severity,
      gameDate: ae.gameDate,
    }));

    const hasNews = stories.length > 0;
    const frontPageStory = stories[0] ?? null;
    const spread1Stories = stories.slice(1, 3);
    const spread2Stories = stories.slice(3, 5);

    let totalPages = 0;
    if (stories.length >= 4) totalPages = 3;
    else if (stories.length >= 2) totalPages = 2;
    else if (stories.length >= 1) totalPages = 1;

    return {
      stories,
      frontPageStory,
      spread1Stories,
      spread2Stories,
      hasNews,
      totalPages,
    };
  }, [eventHistory]);
}
